import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectLogonUrls,
  setIsShowingUserSheet,
  selectUsersName,
  selectCompanyName,
  setGoToResetPassword,
  waitForLoadingToFinish,
} from 'store/appSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { switchRouteTo } from 'hooks/useShuttle';
import { selectHasPeopleTab } from 'store/navigationSlice';
import { selectMasterEmployeeId } from 'store/userSlice';
import { UserAvatarForDrawer } from '../UserAvatar';
import { clearSession, redirectToLogin } from 'lib/router';
import { hasValue } from 'swipeclock-frontend';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

export default function UserMain() {
  return (
    <>
      <Header />
      <Body>
        <User />
      </Body>
      <Footer />
    </>
  );
}

//##################################################################
// ### CHILDREN COMPONENTS
// User is a list of actions
// Header is the user's name and company name
// Footer is a divider and empty space
//##################################################################

function User() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const masterEmployeeId = useSelector(selectMasterEmployeeId);
  const logonUrls = useSelector(selectLogonUrls);
  const hide = useSelector((state) => state.navigation.disabledTopAppBar);
  const hasPeopleTab = useSelector(selectHasPeopleTab);

  const handleGoToEmployeeRecord = () => {
    dispatch(setIsShowingUserSheet({ showing: false }));

    // Without a People tab there's no employees-list route to shuttle
    // through, so go straight to the chrome-free employee record page.
    if (!hasPeopleTab) {
      navigate('/employee-record');
      return;
    }

    const goToEmployeeRecord = () => {
      switchRouteTo({
        to: `/employee/${masterEmployeeId}`,
      });
    };

    if (pathname === '/people/employees-list') {
      goToEmployeeRecord();
    } else {
      navigate('/people/employees-list');
      dispatch(waitForLoadingToFinish(goToEmployeeRecord));
    }
  };

  const handleResetPassword = () => {
    dispatch(setGoToResetPassword({ goToResetPassword: true }));
  };

  const handleSignOut = () => {
    clearSession(dispatch);
    return redirectToLogin(logonUrls);
  };

  return (
    <List dense>
      {!hide && (
        <ListItemButton onClick={handleGoToEmployeeRecord}>
          <ListItemText primary="My Employee Record" />
        </ListItemButton>
      )}

      <ListItemButton onClick={handleResetPassword}>
        <ListItemText primary="Reset Password" />
      </ListItemButton>

      <ListItemButton
        href="https://support.swipeclock.com/workforcehub/s/"
        target="_blank"
      >
        <ListItemText primary="WorkforceHub Knowledge Base" />
      </ListItemButton>

      <ListItemButton onClick={handleSignOut}>
        <ListItemText primary="Sign Out" />
      </ListItemButton>
    </List>
  );
}

function Header() {
  const dispatch = useDispatch();
  const usersName = useSelector(selectUsersName);
  const companyName = useSelector(selectCompanyName);
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing(1),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <UserAvatarForDrawer />
          <Stack direction="column" spacing={0}>
            {hasValue(usersName) && (
              <Typography
                variant="body1"
                sx={{ color: theme.palette.custom.navyBlue.main }}
              >
                {usersName}
              </Typography>
            )}
            {hasValue(companyName) && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: theme.typography.fontWeightLight }}
              >
                {companyName}
              </Typography>
            )}
          </Stack>
        </Stack>
        <IconButton
          onClick={() => dispatch(setIsShowingUserSheet({ showing: false }))}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </>
  );
}

function Footer() {
  return (
    <>
      <Divider />
      <Box sx={{ height: '56px', width: '100%' }} />
    </>
  );
}

//##################################################################
// ### STYLED COMPONENTS
//##################################################################

const Body = styled(Box)(({ _theme }) => ({
  display: 'flex',
  flexGrow: 1,
  '& ul': {
    width: '100%',
  },
}));
